// PortalShare.jsx - Admin button: send portal link to a client via SMS or email
import { useState } from "react";
import { supabase } from "./supabase";

const C = {
  gold:"#b48c3c", goldD:"#8a6f2e", goldBg:"rgba(180,140,60,0.08)", borderGold:"rgba(180,140,60,0.3)",
  green:"#2e8b57", greenL:"rgba(46,139,87,0.12)",
  red:"#c0392b", redL:"rgba(192,57,43,0.10)",
  blue:"#2563eb", blueL:"rgba(37,99,235,0.08)",
  purple:"#7c3aed",
  text:"#1a1a2e", muted:"#6b7280", dim:"#9ca3af", light:"#f3f4f6", border:"#e8e4df", card:"#fff",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

// Wrapper: small button that opens modal
export default function PortalShare({ client }) {
  const [show, setShow] = useState(false);
  if (!client?.id) return null;
  return (
    <>
      <button onClick={()=>setShow(true)} style={{padding:"6px 12px",background:C.purple,color:"#fff",border:"none",borderRadius:6,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:F}}>
        🔑 Send Portal Link
      </button>
      {show && <PortalShareModal client={client} onClose={()=>setShow(false)}/>}
    </>
  );
}

function PortalShareModal({ client, onClose }) {
  const [generated, setGenerated] = useState(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const generate = async (sendVia) => {
    setSending(true); setError("");
    try {
      const { data, error: e } = await supabase.functions.invoke("portal-api", {
        body: { action: "generate", client_id: client.id, send_via: sendVia },
      });
      console.log("[PortalShare] response:", { data, error: e });
      if (e) {
        // Try to extract message from error context
        const msg = e.context?.error || e.message || "Network error";
        throw new Error(msg);
      }
      if (data?.error) throw new Error(data.error);
      if (data?.ok === false) throw new Error(data.error || "Server returned failure");
      if (!data?.url) throw new Error("Server did not return a URL. Check portal-api deployment.");
      setGenerated(data);
    } catch (e) {
      console.error("[PortalShare] error:", e);
      setError(e.message || String(e));
    }
    finally { setSending(false); }
  };

  const copy = () => {
    if (!generated?.url) return;
    navigator.clipboard.writeText(generated.url);
    setCopied(true);
    setTimeout(()=>setCopied(false), 2000);
  };

  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:20,backdropFilter:"blur(4px)"}}>
      <div onClick={e=>e.stopPropagation()} style={{background:"#fff",borderRadius:14,maxWidth:480,width:"100%",padding:24}}>
        <div style={{display:"flex",justifyContent:"space-between",marginBottom:14}}>
          <div>
            <div style={{fontSize:18,fontWeight:800,color:C.purple,fontFamily:S,marginBottom:3}}>🔑 Portal Access</div>
            <div style={{fontSize:11,color:C.muted}}>Send {(client.name || "this client").split(" ")[0]} a magic link to their portal</div>
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",color:C.muted,cursor:"pointer",fontSize:22}}>×</button>
        </div>

        {!generated ? (
          <div>
            <div style={{padding:"14px 16px",background:C.light,borderRadius:10,marginBottom:14,fontSize:11,color:C.muted,lineHeight:1.5}}>
              <strong style={{color:C.text}}>What's a portal link?</strong> It's a private URL that lets the client view their appointments, packages, payments, and sign consent forms. Valid for 30 days. No password required.
            </div>

            <div style={{display:"grid",gap:8,marginBottom:14}}>
              <button onClick={()=>generate("sms")} disabled={sending || !client.phone} style={{padding:"14px 18px",background: sending ? C.light : "linear-gradient(135deg,"+C.blue+", "+C.purple+")", color: sending ? C.muted : "#fff",border:"none",borderRadius:10,fontSize:13,fontWeight:700,cursor: sending||!client.phone ? "not-allowed" : "pointer",fontFamily:F,opacity: !client.phone ? 0.5 : 1,textAlign:"left",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span>📱 Send via SMS{!client.phone && " (no phone)"}</span>
                <span>→</span>
              </button>
              <button onClick={()=>generate("email")} disabled={sending || !client.email} style={{padding:"14px 18px",background: sending ? C.light : "linear-gradient(135deg,"+C.gold+","+C.goldD+")", color: sending ? C.muted : "#fff",border:"none",borderRadius:10,fontSize:13,fontWeight:700,cursor: sending||!client.email ? "not-allowed" : "pointer",fontFamily:F,opacity: !client.email ? 0.5 : 1,textAlign:"left",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span>📧 Send via Email{!client.email && " (no email)"}</span>
                <span>→</span>
              </button>
              <button onClick={()=>generate(null)} disabled={sending} style={{padding:"14px 18px",background:C.light,color:C.text,border:"1px solid "+C.border,borderRadius:10,fontSize:13,fontWeight:700,cursor: sending ? "not-allowed" : "pointer",fontFamily:F,textAlign:"left",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span>🔗 Just generate link (don't send)</span>
                <span>→</span>
              </button>
            </div>

            {error && <div style={{padding:"10px 14px",background:C.redL,color:C.red,borderRadius:8,fontSize:12,fontWeight:600}}>{error}</div>}
          </div>
        ) : (
          <div>
            <div style={{padding:"14px 16px",background:C.greenL,border:"1px solid "+C.green+"40",borderRadius:10,marginBottom:14}}>
              <div style={{fontSize:12,fontWeight:700,color:C.green,marginBottom:4}}>✓ Portal link created!</div>
              <div style={{fontSize:11,color:C.muted}}>
                {generated.sent_via === "sms" ? `📱 Sent via SMS to ${client.phone}` : generated.sent_via === "email" ? `📧 Sent via email to ${client.email}` : "Link generated - share manually"}
                <br/>Expires {new Date(generated.expires_at).toLocaleDateString()}
              </div>
            </div>

            <div style={{padding:"10px 12px",background:C.light,borderRadius:8,marginBottom:10,position:"relative"}}>
              <div style={{fontSize:9,color:C.muted,fontWeight:700,textTransform:"uppercase",marginBottom:4}}>Portal URL</div>
              <div style={{fontSize:11,fontFamily:"monospace",color:C.text,wordBreak:"break-all",paddingRight:80}}>{generated.url}</div>
              <button onClick={copy} style={{position:"absolute",top:8,right:8,padding:"5px 12px",background: copied ? C.green : C.gold,color:"#fff",border:"none",borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:F}}>{copied ? "✓" : "📋 Copy"}</button>
            </div>

            <button onClick={onClose} style={{width:"100%",padding:"11px",background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:F}}>Done</button>
          </div>
        )}
      </div>
    </div>
  );
}
